'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackRSVPStep, trackRSVPConversion, trackFormStart } from '@/lib/analytics';

interface GuestInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  guestCount: number;
  plusOneName: string;
  mealPreference: string;
  dietaryRestrictions: string;
  attendingCeremony: boolean;
  attendingReception: boolean;
  blessingMessage: string;
  allowPublicBlessing: boolean;
}

interface RSVPFormProps {
  onSubmit: (data: GuestInfo) => void;
}

const RSVPForm = ({ onSubmit }: RSVPFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<GuestInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    guestCount: 1,
    plusOneName: '',
    mealPreference: '',
    dietaryRestrictions: '',
    attendingCeremony: true,
    attendingReception: true,
    blessingMessage: '',
    allowPublicBlessing: true,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof GuestInfo, string>>>({});
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
    trackFormStart('rsvp_form');
    const savedData = localStorage.getItem('rsvp_draft');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed.formData || formData);
        setCurrentStep(parsed.step || 1);
      } catch (e) {
        console.error('Failed to parse saved RSVP data');
      }
    }
  }, []);

  useEffect(() => {
    if (isHydrated) {
      localStorage.setItem('rsvp_draft', JSON.stringify({ formData, step: currentStep }));
    }
  }, [formData, currentStep, isHydrated]);

  useEffect(() => {
    setCharCount(formData.blessingMessage.length);
  }, [formData.blessingMessage]);

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof GuestInfo, string>> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\+?[\d\s-()]+$/.test(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    if (step === 2) {
      if (formData.guestCount < 1) newErrors.guestCount = 'At least one guest is required';
      if (formData.guestCount > 1 && !formData.plusOneName.trim()) {
        newErrors.plusOneName = 'Please provide the name of your plus one';
      }
      if (!formData.mealPreference) newErrors.mealPreference = 'Please select a meal preference';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      trackRSVPStep(`step_${currentStep}_completed`);
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateStep(currentStep)) {
      trackRSVPConversion({
        guestCount: formData.guestCount,
        attendingCeremony: formData.attendingCeremony,
        attendingReception: formData.attendingReception,
        mealPreference: formData.mealPreference,
      });
      onSubmit(formData);
      if (isHydrated) {
        localStorage.removeItem('rsvp_draft');
      }
    }
  };

  const handleInputChange = (field: keyof GuestInfo, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isHydrated) {
    return (
      <div className="w-full max-w-2xl mx-auto bg-card rounded-lg shadow-sacred-lg p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-surface rounded w-3/4"></div>
          <div className="h-4 bg-surface rounded w-1/2"></div>
          <div className="space-y-4">
            <div className="h-12 bg-surface rounded"></div>
            <div className="h-12 bg-surface rounded"></div>
            <div className="h-12 bg-surface rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const totalSteps = 3;
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className="w-full max-w-2xl mx-auto bg-card rounded-lg shadow-sacred-lg p-6 sm:p-8">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="font-inter text-sm text-muted-foreground">
            Step {currentStep} of {totalSteps}
          </span>
          <span className="font-inter text-sm text-primary font-medium">
            {Math.round(progress)}% Complete
          </span>
        </div>
        <div className="w-full h-2 bg-surface rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="font-playfair text-2xl font-semibold text-foreground mb-2">
                Personal Information
              </h2>
              <p className="font-inter text-sm text-muted-foreground">
                Please provide your contact details so we can keep you updated
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block font-inter text-sm font-medium text-foreground mb-2">
                  First Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`w-full px-4 py-3 rounded-md border ${
                    errors.firstName ? 'border-error' : 'border-border'
                  } bg-input text-foreground font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <p className="mt-1 font-inter text-xs text-error flex items-center">
                    <Icon name="ExclamationCircleIcon" size={14} className="mr-1" />
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block font-inter text-sm font-medium text-foreground mb-2">
                  Last Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`w-full px-4 py-3 rounded-md border ${
                    errors.lastName ? 'border-error' : 'border-border'
                  } bg-input text-foreground font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <p className="mt-1 font-inter text-xs text-error flex items-center">
                    <Icon name="ExclamationCircleIcon" size={14} className="mr-1" />
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block font-inter text-sm font-medium text-foreground mb-2">
                Email Address <span className="text-error">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.email ? 'border-error' : 'border-border'
                } bg-input text-foreground font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                placeholder="your.email@example.com"
              />
              {errors.email && (
                <p className="mt-1 font-inter text-xs text-error flex items-center">
                  <Icon name="ExclamationCircleIcon" size={14} className="mr-1" />
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block font-inter text-sm font-medium text-foreground mb-2">
                Phone Number <span className="text-error">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.phone ? 'border-error' : 'border-border'
                } bg-input text-foreground font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                placeholder="+1 (555) 123-4567"
              />
              {errors.phone && (
                <p className="mt-1 font-inter text-xs text-error flex items-center">
                  <Icon name="ExclamationCircleIcon" size={14} className="mr-1" />
                  {errors.phone}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Attendance Details */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="font-playfair text-2xl font-semibold text-foreground mb-2">
                Attendance Details
              </h2>
              <p className="font-inter text-sm text-muted-foreground">
                Let us know who will be joining us for this blessed celebration
              </p>
            </div>

            <div>
              <label htmlFor="guestCount" className="block font-inter text-sm font-medium text-foreground mb-2">
                Number of Guests <span className="text-error">*</span>
              </label>
              <select
                id="guestCount"
                value={formData.guestCount}
                onChange={(e) => handleInputChange('guestCount', parseInt(e.target.value))}
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.guestCount ? 'border-error' : 'border-border'
                } bg-input text-foreground font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
              >
                <option value={1}>Just me</option>
                <option value={2}>Me + 1 guest</option>
                <option value={3}>Me + 2 guests</option>
                <option value={4}>Me + 3 guests</option>
              </select>
              {errors.guestCount && (
                <p className="mt-1 font-inter text-xs text-error flex items-center">
                  <Icon name="ExclamationCircleIcon" size={14} className="mr-1" />
                  {errors.guestCount}
                </p>
              )}
            </div>

            {formData.guestCount > 1 && (
              <div>
                <label htmlFor="plusOneName" className="block font-inter text-sm font-medium text-foreground mb-2">
                  Guest Name(s) <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  id="plusOneName"
                  value={formData.plusOneName}
                  onChange={(e) => handleInputChange('plusOneName', e.target.value)}
                  className={`w-full px-4 py-3 rounded-md border ${
                    errors.plusOneName ? 'border-error' : 'border-border'
                  } bg-input text-foreground font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
                  placeholder="Enter guest name(s)"
                />
                {errors.plusOneName && (
                  <p className="mt-1 font-inter text-xs text-error flex items-center">
                    <Icon name="ExclamationCircleIcon" size={14} className="mr-1" />
                    {errors.plusOneName}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="attendingCeremony"
                  checked={formData.attendingCeremony}
                  onChange={(e) => handleInputChange('attendingCeremony', e.target.checked)}
                  className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                />
                <label htmlFor="attendingCeremony" className="ml-3 font-inter text-sm text-foreground">
                  I will attend the ceremony
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="attendingReception"
                  checked={formData.attendingReception}
                  onChange={(e) => handleInputChange('attendingReception', e.target.checked)}
                  className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                />
                <label htmlFor="attendingReception" className="ml-3 font-inter text-sm text-foreground">
                  I will attend the reception
                </label>
              </div>
            </div>

            <div>
              <label htmlFor="mealPreference" className="block font-inter text-sm font-medium text-foreground mb-2">
                Meal Preference <span className="text-error">*</span>
              </label>
              <select
                id="mealPreference"
                value={formData.mealPreference}
                onChange={(e) => handleInputChange('mealPreference', e.target.value)}
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.mealPreference ? 'border-error' : 'border-border'
                } bg-input text-foreground font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200`}
              >
                <option value="">Select your meal preference</option>
                <option value="non_vegetarian">Non-Vegetarian</option>
                <option value="vegetarian">Vegetarian</option>
                <option value="pescatarian">Pescatarian (Fish)</option>
                <option value="vegan">Vegan</option>
                <option value="other">Other</option>
              </select>
              {errors.mealPreference && (
                <p className="mt-1 font-inter text-xs text-error flex items-center">
                  <Icon name="ExclamationCircleIcon" size={14} className="mr-1" />
                  {errors.mealPreference}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="dietaryRestrictions" className="block font-inter text-sm font-medium text-foreground mb-2">
                Dietary Restrictions or Allergies
              </label>
              <textarea
                id="dietaryRestrictions"
                value={formData.dietaryRestrictions}
                onChange={(e) => handleInputChange('dietaryRestrictions', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 rounded-md border border-border bg-input text-foreground font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 resize-none"
                placeholder="Please let us know of any dietary restrictions or allergies"
              />
            </div>
          </div>
        )}

        {/* Step 3: Blessing Message */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="font-playfair text-2xl font-semibold text-foreground mb-2">
                Share Your Blessing
              </h2>
              <p className="font-inter text-sm text-muted-foreground">
                Your words of encouragement and prayer mean the world to us
              </p>
            </div>

            <div>
              <label htmlFor="blessingMessage" className="block font-inter text-sm font-medium text-foreground mb-2">
                Blessing Message (Optional)
              </label>
              <textarea
                id="blessingMessage"
                value={formData.blessingMessage}
                onChange={(e) => handleInputChange('blessingMessage', e.target.value)}
                rows={6}
                maxLength={500}
                className="w-full px-4 py-3 rounded-md border border-border bg-input text-foreground font-inter text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 resize-none"
                placeholder="Share your prayers, blessings, or words of wisdom for our marriage..."
              />
              <div className="flex justify-between items-center mt-2">
                <p className="font-inter text-xs text-muted-foreground">
                  Your message will be reviewed before being shared publicly
                </p>
                <p className={`font-inter text-xs ${charCount > 450 ? 'text-warning' : 'text-muted-foreground'}`}>
                  {charCount}/500
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                id="allowPublicBlessing"
                checked={formData.allowPublicBlessing}
                onChange={(e) => handleInputChange('allowPublicBlessing', e.target.checked)}
                className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary mt-0.5"
              />
              <label htmlFor="allowPublicBlessing" className="ml-3 font-inter text-sm text-foreground">
                I give permission for my blessing message to be shared publicly on the website (your name will be displayed with your message)
              </label>
            </div>

            <div className="bg-surface rounded-lg p-4 border border-border">
              <div className="flex items-start space-x-3">
                <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-inter text-sm text-foreground">
                    <span className="font-semibold">Privacy Notice:</span> Your contact information will only be used for wedding coordination and will never be shared publicly or with third parties.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center space-x-2 px-6 py-3 rounded-md border border-border bg-surface text-foreground font-inter text-sm font-medium hover:bg-input transition-all duration-200"
            >
              <Icon name="ChevronLeftIcon" size={18} />
              <span>Back</span>
            </button>
          ) : (
            <div></div>
          )}

          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              className="flex items-center space-x-2 px-6 py-3 rounded-md bg-primary text-primary-foreground font-inter text-sm font-medium hover:bg-primary/90 transition-all duration-200 shadow-md"
            >
              <span>Continue</span>
              <Icon name="ChevronRightIcon" size={18} />
            </button>
          ) : (
            <button
              type="submit"
              className="flex items-center space-x-2 px-6 py-3 rounded-md bg-primary text-primary-foreground font-inter text-sm font-medium hover:bg-primary/90 transition-all duration-200 shadow-md"
            >
              <Icon name="CheckCircleIcon" size={18} />
              <span>Submit RSVP</span>
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default RSVPForm;